import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";


interface DailyLineGraphProps {
    height: number;
    type: "user" | "chat" | "message" | "messagePerChat";
    tooltipLabel: string;
    counts?: {
        year: string;
        month: string;
        day: string;
        messagingUserCount: number;
        chatCount: number;
        messageCount: number;
        fallbackCount: number;
    }[];
}

const DailyLineGraph: React.FC<DailyLineGraphProps> = ({ height, type, tooltipLabel, counts = [] }) => {
    const intl = useIntl();

    const dates = counts.map(count => moment(`${count.year}-${count.month}-${count.day}`, "YYYY-MM-DD").valueOf());
    const values = counts.map(count => {
        switch (type) {
            case "user":
                return count.messagingUserCount;
            case "chat":
                return count.chatCount;
            case "message":
                return count.messageCount - count.fallbackCount;
            case "messagePerChat":
                if (!count.chatCount) {
                    return 0;
                }
                return Number(((count.messageCount - count.fallbackCount) / count.chatCount).toFixed(2));
            default:
                return 0;
        }
    });

    const ticks = useMemo(() => {
        if (dates.length <= 10) {
            return dates;
        } else if (dates.length < 50) {
            const tick = Math.ceil(dates.length / 10);
            return dates.filter((_, index) => index % tick === 0);
        } else {
            return dates.filter((_, index) => index % 5 === 0);
        }
    }, [dates]);

    if (!values.some(v => v > 0)) {
        return (
            <div className="w-full flex items-center justify-center" style={{ height }}>
                <div className="text-black-400 text-[14px] font-[400]">
                    {intl.formatMessage({ id: "b0013" })}
                </div>
            </div>
        );
    }
    const ariaLabel = `${tooltipLabel} ${intl.formatMessage({ id: "i01008" })}`;
    return (
        <div className="relative w-full"
             style={{ height }}
             role="img"
             aria-label={ariaLabel}
        >
            <Chart type="line"
                   height="100%"
                   series={[{
                       name: tooltipLabel,
                       data: values
                   }]}
                   options={createChartOptions(
                       dates,
                       ticks,
                       intl.formatMessage({ id: "i01007" }),
                       tooltipLabel
                   )}
            />
        </div>
    );
};

const createChartOptions = (
    dates: number[],
    ticks: number[],
    dateFormat: string,
    viewsLabel: string
): ApexOptions => ({
    chart: {
        toolbar: { show: false },
        zoom: { enabled: false }
    },
    colors: ["#441A98"],
    stroke: {
        width: 3,
        curve: 'straight'
    },
    markers: {
        size: 0.1,
        strokeWidth: 1,
        strokeColors: ["#441A98"],
        hover: {
            size: 6
        }
    },
    dataLabels: { enabled: false },
    tooltip: {
        enabled: true,
        cssClass: "!shadow-none",
        custom: ({ series, seriesIndex, dataPointIndex }) => {
            const date = moment(dates[dataPointIndex]).format(dateFormat);
            const value = series[seriesIndex][dataPointIndex];
            
            return (
                `<div class="w-[120px]">
                    <div class="h-7.5 flex items-center justify-center rounded-t-[6px] bg-chat42 text-white text-[12px] font-[400] leading-none">
                        ${date}
                    </div>
                    <div class="h-7.5 flex items-center justify-center rounded-b-[6px] bg-white text-chat42 text-[12px] font-[400] leading-none">
                        ${viewsLabel} ${value}
                    </div>
                </div>`
            );
        }
    },
    xaxis: {
        type: "datetime",
        categories: dates,
        labels: {
            datetimeUTC: false,
            formatter: (_, timestamp, opts) => {
                if (timestamp && ticks.includes(timestamp)) {
                    return opts.dateFormatter(new Date(timestamp!), "M/d");
                }
                return "";
            },
            style: {
                cssClass: "text-black-400 text-[11px] font-[400] leading-none",
                fontFamily: "Pretendard"
            },
            rotate: 0
        },
        tooltip: { enabled: false },
        tickAmount: dates.length - 1
    },
    yaxis: {
        labels: {
            formatter: (value: number) => (value % 1 === 0 ? value.toString() : ""),
            minWidth: 20,
            maxWidth: 20,
            style: {
                cssClass: "text-black-400 text-[11px] font-[400] leading-none",
                fontFamily: "Pretendard"
            }
        },
        min: 0,
        forceNiceScale: true
    }
});

export default DailyLineGraph;
